/**
 * Resource versioning hooks (task 725).
 *
 * Report: `project/reports/innovate-service-published-resource-versioning.md`.
 *
 * Two concerns live here, both read through the app's standard service layer
 * (never a raw fetch):
 *
 *   1. The **account setting** — the one-way, account-wide adoption of the
 *      publishing practice. Every versioning surface in the app consults it
 *      first, because an Account that has not adopted the practice must see
 *      zero versioning UI. A failed or missing settings read resolves to the
 *      dormant answer rather than an error state: the app's job when it cannot
 *      confirm adoption is to look exactly like it does today.
 *   2. The **per-resource version store** — status, history, one version whole,
 *      publish preview, publish.
 *
 * Every per-resource query is `enabled` only once versioning is known to be on,
 * so a dormant Account makes no version calls at all.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { accountVersioningApi, resourceVersionsApi } from '@/services/api';
import type {
  AccountVersioningSettings,
  PublishRequest,
  ResourceVersionStatusView,
  VersionedEntityType,
} from '@/types/versioning';

/** Query keys the versioning surfaces share. */
export const ACCOUNT_VERSIONING_QUERY_KEY = ['account', 'versioning'] as const;

export const resourceVersionsQueryKey = (
  entityType: VersionedEntityType,
  entityId: string | undefined
) => ['resource-versions', entityType, entityId] as const;

export const resourceVersionStatusQueryKey = (
  entityType: VersionedEntityType,
  entityId: string | undefined
) => ['resource-version-status', entityType, entityId] as const;

/** The dormant answer — what every surface renders when adoption is unknown. */
const DORMANT_SETTINGS: AccountVersioningSettings = {
  accountId: '',
  versioningEnabled: false,
  versioningEnabledAt: null,
  versioningEnabledBy: null,
};

/** The dormant status — no version status is shown for it (task 979). */
export const DORMANT_STATUS: ResourceVersionStatusView = {
  state: 'live-editing',
  versioningEnabled: false,
  publishedVersion: null,
  workingVersion: null,
};

/**
 * The Account's adoption state.
 *
 * Fails to the dormant answer rather than to an error: while the versioning
 * routes are rolling out, a 404 here must read as "this Account has not adopted
 * the practice", which is indistinguishable from the truth for every Account
 * that has not.
 */
export const useAccountVersioning = () =>
  useQuery({
    queryKey: ACCOUNT_VERSIONING_QUERY_KEY,
    queryFn: () =>
      accountVersioningApi
        .get()
        .then((res) => res.data.data)
        .catch(() => DORMANT_SETTINGS),
    // Adoption is one-way and rare; there is nothing to re-poll for.
    staleTime: 5 * 60_000,
    retry: false,
  });

/** Convenience read: is the publishing practice adopted? */
export const useVersioningEnabled = (): { enabled: boolean; isResolved: boolean } => {
  const { data, isSuccess } = useAccountVersioning();
  return { enabled: data?.versioningEnabled === true, isResolved: isSuccess };
};

/**
 * Adopt the publishing practice. Writes the server's answer into the cache so
 * the settings section re-renders from recorded truth, and drops every cached
 * per-resource status so the indicators pick up their tracked state.
 */
export const useEnableVersioning = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => accountVersioningApi.enable().then((res) => res.data.data),
    onSuccess: (settings) => {
      queryClient.setQueryData(ACCOUNT_VERSIONING_QUERY_KEY, settings);
      void queryClient.invalidateQueries({ queryKey: ['resource-version-status'] });
    },
  });
};

/**
 * The four-state status for one resource.
 *
 * Returns the dormant status without a network call while the Account has not
 * adopted the practice — the state for which the indicator shows no version
 * status at all.
 */
export const useResourceVersionStatus = (
  entityType: VersionedEntityType,
  entityId: string | undefined
) => {
  const { enabled } = useVersioningEnabled();

  const query = useQuery({
    queryKey: resourceVersionStatusQueryKey(entityType, entityId),
    queryFn: () =>
      resourceVersionsApi.getStatus(entityType, entityId!).then((res) => res.data.data),
    enabled: enabled && !!entityId,
    retry: false,
  });

  return {
    ...query,
    status: enabled ? query.data ?? DORMANT_STATUS : DORMANT_STATUS,
  };
};

/** Version history for a resource, newest first. */
export const useResourceVersions = (
  entityType: VersionedEntityType,
  entityId: string | undefined,
  options?: { enabled?: boolean }
) => {
  const { enabled: versioningEnabled } = useVersioningEnabled();

  return useQuery({
    queryKey: resourceVersionsQueryKey(entityType, entityId),
    queryFn: () => resourceVersionsApi.list(entityType, entityId!).then((res) => res.data.data),
    enabled: versioningEnabled && !!entityId && options?.enabled !== false,
    retry: false,
  });
};

/**
 * One version whole — its frozen document.
 *
 * The version travels as its integer pair, never as a decimal string: `1.10` is
 * the tenth minor of major 1, and a float would silently make it the first.
 */
export const useResourceVersion = (
  entityType: VersionedEntityType,
  entityId: string | undefined,
  version: { major: number; minor: number } | undefined
) =>
  useQuery({
    queryKey: ['resource-version', entityType, entityId, version?.major, version?.minor],
    queryFn: () =>
      resourceVersionsApi
        .get(entityType, entityId!, version!.major, version!.minor)
        .then((res) => res.data.data),
    enabled: !!entityId && !!version,
    // Published versions are immutable, so a fetched one never goes stale.
    staleTime: Infinity,
    retry: false,
  });

/**
 * The publish dry run: the version this would take, whether it is the first
 * publish, what cascades along, and what an endpoint version would pin.
 *
 * `majorBump` is part of the REQUEST, not something the dialog computes — the
 * service owns version arithmetic — so toggling the option refetches rather
 * than deriving a number the client guessed.
 */
export const usePublishPreview = (
  entityType: VersionedEntityType,
  entityId: string | undefined,
  options?: { enabled?: boolean; majorBump?: boolean }
) =>
  useQuery({
    queryKey: [
      'resource-version-publish-preview',
      entityType,
      entityId,
      options?.majorBump === true,
    ],
    queryFn: () =>
      resourceVersionsApi
        .previewPublish(entityType, entityId!, options?.majorBump === true)
        .then((res) => res.data.data),
    enabled: !!entityId && options?.enabled !== false,
    // A preview is a decision input; never serve a cached one.
    staleTime: 0,
    gcTime: 0,
    retry: false,
  });

/**
 * Publish the working version.
 *
 * Deliberately has NO `onError` (the task-388 precedent): the rejection travels
 * to whoever called `mutateAsync`, so the dialog can render the reason in place
 * instead of the failure disappearing into a handler here.
 */
export const usePublishResource = (
  entityType: VersionedEntityType,
  entityId: string | undefined
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: PublishRequest) =>
      resourceVersionsApi.publish(entityType, entityId!, data).then((res) => res.data.data),
    onSuccess: () => {
      // The cascade can freeze other resources' versions too, so every status
      // and history in the cache is suspect after a publish.
      void queryClient.invalidateQueries({ queryKey: ['resource-version-status'] });
      void queryClient.invalidateQueries({ queryKey: ['resource-versions'] });
    },
  });
};
